import type { Category, Product, ProductOption, Review } from '@/types';
import type { CatalogIconKey } from '@/lib/icons';

export const categoriesSeed: Omit<Category, 'productCount'>[] = [
  { id: 'cat_electronics', slug: 'electronics', name: 'Electronics', description: 'Audio, wearables and everyday tech built to last.', icon: 'smartphone', hue: 235 },
  { id: 'cat_fashion', slug: 'fashion', name: 'Fashion', description: 'Modern essentials, bags and accessories.', icon: 'shirt', hue: 215 },
  { id: 'cat_home', slug: 'home-living', name: 'Home & Living', description: 'Lighting, bedding and decor for a calmer home.', icon: 'armchair', hue: 28 },
  { id: 'cat_beauty', slug: 'beauty-personal-care', name: 'Beauty & Personal Care', description: 'Skincare, grooming and self-care favourites.', icon: 'sparkles', hue: 335 },
  { id: 'cat_sports', slug: 'sports', name: 'Sports & Fitness', description: 'Footwear and gear for every workout.', icon: 'dumbbell', hue: 190 },
  { id: 'cat_toys', slug: 'toys-games', name: 'Toys & Games', description: 'Creative play for kids and game nights for everyone.', icon: 'gamepad', hue: 50 },
];

interface Seed {
  n: string; brand: string; cat: string; price: number; cmp?: number; icon: CatalogIconKey; hue: number;
  rating: number; reviews: number; stock: number; feat?: boolean; desc: string; hl: string[];
  specs: [string, string][]; opts?: ProductOption[]; tags?: string[]; days: number;
}

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const seeds: Seed[] = [
  {
    n: 'Wireless Noise Cancelling Headphones', brand: 'Aurora', cat: 'cat_electronics', price: 3499, cmp: 4999, icon: 'headphones', hue: 240,
    rating: 4.6, reviews: 1248, stock: 42, feat: true, days: 4,
    desc: 'Immerse yourself in sound with our premium noise cancelling headphones. Hybrid active noise cancellation shuts out the commute, the office and everything in between, while custom 40mm drivers deliver deep bass and crystal-clear highs. Memory-foam ear cushions and a featherweight frame keep you comfortable through long flights and longer workdays.\n\nBattery life stretches to 40 hours with ANC on, and a 10-minute quick charge gives you 5 more hours of playback. Dual-device Bluetooth 5.3 lets you switch between your laptop and phone seamlessly, and the built-in beamforming microphones keep calls clear even in busy streets.',
    hl: ['Active Noise Cancellation (ANC) with Transparency mode', 'Up to 40 hours battery life, 10-min quick charge', 'Bluetooth 5.3 with multipoint connection', 'Comfortable memory-foam ear cushions', 'Foldable design with premium carry case'],
    specs: [['Driver', '40mm dynamic'], ['Battery', 'Up to 40 hrs (ANC on)'], ['Connectivity', 'Bluetooth 5.3, 3.5mm AUX'], ['Weight', '254 g'], ['Warranty', '1 year']],
    opts: [{ name: 'Colour', values: ['Midnight Black', 'Cloud Silver', 'Deep Blue'] }], tags: ['audio', 'wireless', 'anc'],
  },
  {
    n: 'Smart Watch Series 5', brand: 'Pulse', cat: 'cat_electronics', price: 5999, cmp: 7999, icon: 'watch', hue: 260,
    rating: 4.6, reviews: 864, stock: 30, feat: true, days: 9,
    desc: 'A bright AMOLED display, precise health tracking and a week of battery life in a slim aluminium body. Track heart rate, SpO2, sleep and 100+ workout modes, take calls from your wrist and stay on top of notifications without reaching for your phone.',
    hl: ['1.45" AMOLED always-on display', 'Heart rate, SpO2 and sleep tracking', 'Built-in GPS with 100+ sport modes', '7-day battery, 5 ATM water resistance'],
    specs: [['Display', '1.45" AMOLED, 466×466'], ['Battery', 'Up to 7 days'], ['Water resistance', '5 ATM'], ['Compatibility', 'Android 8+, iOS 14+']],
    opts: [{ name: 'Strap', values: ['Graphite', 'Sand', 'Ocean'] }, { name: 'Size', values: ['42 mm', '46 mm'] }], tags: ['wearable', 'fitness'],
  },
  {
    n: 'Wireless Earbuds Pro', brand: 'Aurora', cat: 'cat_electronics', price: 2499, cmp: 3499, icon: 'headphones', hue: 250,
    rating: 4.5, reviews: 2011, stock: 120, feat: true, days: 14,
    desc: 'Pocket-sized earbuds with adaptive noise cancellation, spatial audio and a wireless charging case. IPX5 sweat resistance makes them workout-ready.',
    hl: ['Adaptive ANC + Transparency', '28 hours total playback with case', 'IPX5 sweat and splash resistant', 'Wireless charging case'],
    specs: [['Driver', '11mm graphene'], ['Battery', '7 hrs + 21 hrs case'], ['Connectivity', 'Bluetooth 5.3'], ['Charging', 'USB-C / Qi']],
    opts: [{ name: 'Colour', values: ['White', 'Black'] }], tags: ['audio', 'wireless'],
  },
  {
    n: 'Portable Bluetooth Speaker', brand: 'Aurora', cat: 'cat_electronics', price: 1999, cmp: 2699, icon: 'speaker', hue: 220,
    rating: 4.4, reviews: 642, stock: 58, days: 30,
    desc: 'Room-filling 360° sound in a rugged, waterproof body. 20 hours of playtime and a built-in power bank for your phone.',
    hl: ['360° stereo sound, 20W output', 'IP67 waterproof and dustproof', '20 hours playtime', 'Doubles as a power bank'],
    specs: [['Output', '20W RMS'], ['Battery', '20 hrs'], ['Rating', 'IP67'], ['Weight', '640 g']],
    opts: [{ name: 'Colour', values: ['Charcoal', 'Teal', 'Coral'] }], tags: ['audio', 'outdoor'],
  },
  {
    n: 'Ergonomic Wireless Mouse', brand: 'Nimbus', cat: 'cat_electronics', price: 799, cmp: 1199, icon: 'mouse', hue: 200,
    rating: 4.3, reviews: 389, stock: 210, days: 45,
    desc: 'Silent-click ergonomic mouse with adjustable DPI, 18-month battery life and a dual-mode 2.4 GHz / Bluetooth connection.',
    hl: ['Silent clicks, 4 DPI levels up to 3200', '18-month battery life', 'Dual mode: 2.4 GHz + Bluetooth'],
    specs: [['Sensor', 'Optical 3200 DPI'], ['Battery', '1× AA (18 months)'], ['Buttons', '6']], tags: ['accessories'],
  },
  {
    n: 'Aluminium Laptop Stand', brand: 'Nimbus', cat: 'cat_electronics', price: 1299, cmp: 1799, icon: 'laptop', hue: 210,
    rating: 4.7, reviews: 512, stock: 8, days: 60,
    desc: 'A foldable, height-adjustable aluminium stand that lifts your screen to eye level and keeps your laptop cool. Supports up to 10 kg.',
    hl: ['6 height levels, folds flat', 'Supports laptops from 10" to 17"', 'Anti-slip silicone pads'],
    specs: [['Material', 'Aircraft-grade aluminium'], ['Max load', '10 kg'], ['Weight', '480 g']], tags: ['workspace'],
  },
  {
    n: 'Classic Denim Jacket', brand: 'Northline', cat: 'cat_fashion', price: 2799, cmp: 3999, icon: 'shirt', hue: 215,
    rating: 4.5, reviews: 276, stock: 36, feat: true, days: 12,
    desc: 'A timeless mid-weight denim jacket in stonewashed indigo with a slightly relaxed fit. Built from durable cotton denim with a soft-brushed interior.',
    hl: ['100% cotton mid-weight denim', 'Relaxed regular fit', 'Metal shank buttons, four pockets'],
    specs: [['Fabric', '100% cotton denim'], ['Fit', 'Regular'], ['Care', 'Machine wash cold']],
    opts: [{ name: 'Size', values: ['S', 'M', 'L', 'XL'] }], tags: ['jacket', 'unisex'],
  },
  {
    n: 'Cotton Crew T-Shirt (Pack of 3)', brand: 'Northline', cat: 'cat_fashion', price: 899, cmp: 1499, icon: 'shirt', hue: 200,
    rating: 4.4, reviews: 1503, stock: 300, days: 20,
    desc: 'Three everyday tees in combed cotton with a soft hand-feel and a shape that holds after every wash.',
    hl: ['Pack of 3 in coordinated colours', '180 GSM combed cotton', 'Pre-shrunk, tagless neck'],
    specs: [['Fabric', '100% combed cotton'], ['GSM', '180'], ['Fit', 'Regular']],
    opts: [{ name: 'Size', values: ['S', 'M', 'L', 'XL', 'XXL'] }], tags: ['basics'],
  },
  {
    n: 'Leather Travel Backpack', brand: 'Wayfarer', cat: 'cat_fashion', price: 3199, cmp: 4499, icon: 'backpack', hue: 25,
    rating: 4.7, reviews: 418, stock: 22, feat: true, days: 8,
    desc: 'A 28L vegan-leather backpack with a padded 16" laptop sleeve, hidden anti-theft pocket and trolley strap. Smart enough for the office, tough enough for the weekend.',
    hl: ['28L capacity, fits 16" laptop', 'Water-resistant vegan leather', 'Hidden anti-theft back pocket', 'Trolley sleeve'],
    specs: [['Capacity', '28 L'], ['Material', 'Vegan leather'], ['Weight', '980 g']],
    opts: [{ name: 'Colour', values: ['Espresso', 'Black'] }], tags: ['bags', 'travel'],
  },
  {
    n: 'Polarised Aviator Sunglasses', brand: 'Wayfarer', cat: 'cat_fashion', price: 1499, cmp: 2199, icon: 'glasses', hue: 45,
    rating: 4.2, reviews: 198, stock: 64, days: 70,
    desc: 'Lightweight metal-frame aviators with polarised, UV400 lenses that cut glare on the road and by the water.',
    hl: ['Polarised UV400 lenses', 'Lightweight titanium-alloy frame', 'Includes hard case and cloth'],
    specs: [['Lens', 'Polarised, UV400'], ['Frame', 'Alloy'], ['Width', '140 mm']], tags: ['accessories'],
  },
  {
    n: 'Ceramic Table Lamp', brand: 'Hearth', cat: 'cat_home', price: 1899, cmp: 2599, icon: 'lamp', hue: 30,
    rating: 4.6, reviews: 143, stock: 27, feat: true, days: 15,
    desc: 'Hand-glazed ceramic base with a linen shade that casts a warm, diffused glow. Comes with a dimmable warm-white LED bulb.',
    hl: ['Hand-glazed ceramic base', 'Linen shade, warm diffused light', 'Dimmable LED bulb included'],
    specs: [['Height', '42 cm'], ['Bulb', 'E27 LED, 9W, 2700K'], ['Cable', '1.8 m with inline switch']],
    opts: [{ name: 'Finish', values: ['Sand', 'Sage', 'Ink'] }], tags: ['lighting'],
  },
  {
    n: 'Cotton Bedsheet Set (King)', brand: 'Hearth', cat: 'cat_home', price: 1599, cmp: 2499, icon: 'bed', hue: 20,
    rating: 4.5, reviews: 731, stock: 75, days: 25,
    desc: '300 thread-count percale cotton bedsheet set: one fitted king sheet and two pillow covers. Crisp, breathable and gets softer with every wash.',
    hl: ['300 thread-count cotton percale', '1 king fitted sheet + 2 pillow covers', 'Deep pockets fit up to 15" mattresses'],
    specs: [['Size', 'King (108" × 108")'], ['Thread count', '300'], ['Care', 'Machine wash']],
    opts: [{ name: 'Colour', values: ['White', 'Stone', 'Navy'] }], tags: ['bedroom'],
  },
  {
    n: 'Soy Wax Scented Candle Set', brand: 'Hearth', cat: 'cat_home', price: 699, cmp: 999, icon: 'flame', hue: 15,
    rating: 4.3, reviews: 254, stock: 140, days: 40,
    desc: 'Set of three hand-poured soy candles in sandalwood, sea salt and jasmine. 35 hours of clean burn each.',
    hl: ['3 × 180g hand-poured soy candles', '35-hour burn time each', 'Cotton wicks, phthalate-free fragrance'],
    specs: [['Weight', '3 × 180 g'], ['Burn time', '35 hrs each'], ['Wax', '100% soy']], tags: ['decor', 'gift'],
  },
  {
    n: 'Ergonomic Mesh Office Chair', brand: 'Hearth', cat: 'cat_home', price: 8999, cmp: 12999, icon: 'armchair', hue: 220,
    rating: 4.4, reviews: 97, stock: 5, days: 50,
    desc: 'Breathable mesh back, adjustable lumbar support and 4D armrests for all-day comfort. Tilt lock and 135° recline.',
    hl: ['Adjustable lumbar support', '4D armrests and headrest', 'Tilt lock, 135° recline'],
    specs: [['Max load', '120 kg'], ['Warranty', '3 years'], ['Assembly', 'Required (tools included)']], tags: ['workspace'],
  },
  {
    n: 'Vitamin C Brightening Serum', brand: 'Lumière', cat: 'cat_beauty', price: 649, cmp: 899, icon: 'droplet', hue: 335,
    rating: 4.5, reviews: 1867, stock: 180, feat: true, days: 10,
    desc: 'A lightweight 15% Vitamin C serum with hyaluronic acid and ferulic acid that visibly brightens and evens skin tone in 4 weeks.',
    hl: ['15% stabilised Vitamin C', 'Hyaluronic acid for hydration', 'Fragrance-free, dermatologist tested'],
    specs: [['Volume', '30 ml'], ['Skin type', 'All skin types'], ['Shelf life', '12 months']], tags: ['skincare'],
  },
  {
    n: 'Matte Lipstick Collection (Set of 4)', brand: 'Lumière', cat: 'cat_beauty', price: 999, cmp: 1599, icon: 'sparkles', hue: 350,
    rating: 4.4, reviews: 622, stock: 96, days: 35,
    desc: 'Four richly pigmented, long-wear matte shades that feel weightless and stay put for up to 12 hours.',
    hl: ['4 best-selling shades', 'Up to 12-hour wear', 'Enriched with vitamin E'],
    specs: [['Finish', 'Matte'], ['Weight', '4 × 3.5 g']], tags: ['makeup'],
  },
  {
    n: 'Precision Beard Trimmer', brand: 'Groom&Co', cat: 'cat_beauty', price: 1799, cmp: 2499, icon: 'scissors', hue: 205,
    rating: 4.6, reviews: 905, stock: 44, days: 18,
    desc: 'Cordless trimmer with 40 length settings, self-sharpening steel blades and 90 minutes of runtime on a 1-hour charge.',
    hl: ['40 precise length settings (0.5–20 mm)', 'Self-sharpening stainless blades', '90 min runtime, USB-C charging'],
    specs: [['Runtime', '90 min'], ['Charge time', '1 hr'], ['Waterproof', 'IPX7']], tags: ['grooming'],
  },
  {
    n: 'Cushioned Running Shoes', brand: 'Stride', cat: 'cat_sports', price: 2499, cmp: 3499, icon: 'footprints', hue: 215,
    rating: 4.3, reviews: 1112, stock: 60, feat: true, days: 6,
    desc: 'Responsive foam midsole, breathable engineered mesh and a grippy outsole make these an all-rounder for daily runs and gym sessions.',
    hl: ['Responsive cushioning midsole', 'Breathable engineered mesh upper', 'High-traction rubber outsole'],
    specs: [['Drop', '8 mm'], ['Weight', '265 g (UK 9)'], ['Use', 'Road running, gym']],
    opts: [{ name: 'Size (UK)', values: ['6', '7', '8', '9', '10', '11'] }, { name: 'Colour', values: ['Blue/White', 'Black/Red'] }], tags: ['footwear'],
  },
  {
    n: 'Non-Slip Yoga Mat (6mm)', brand: 'Stride', cat: 'cat_sports', price: 899, cmp: 1299, icon: 'dumbbell', hue: 170,
    rating: 4.5, reviews: 786, stock: 130, days: 28,
    desc: 'Extra-cushioned 6mm TPE mat with a textured non-slip surface and alignment guides. Includes a carry strap.',
    hl: ['6 mm cushioning', 'Dual-texture non-slip surface', 'Carry strap included'],
    specs: [['Size', '183 × 61 cm'], ['Thickness', '6 mm'], ['Material', 'TPE, eco-friendly']],
    opts: [{ name: 'Colour', values: ['Lavender', 'Forest', 'Slate'] }], tags: ['yoga'],
  },
  {
    n: 'Adjustable Dumbbell Set (20 kg)', brand: 'Stride', cat: 'cat_sports', price: 3299, cmp: 4499, icon: 'dumbbell', hue: 200,
    rating: 4.6, reviews: 329, stock: 14, days: 42,
    desc: 'Space-saving adjustable dumbbells that replace a rack of weights. Quick-lock collars, 2–10 kg per hand.',
    hl: ['2 kg–10 kg per dumbbell', 'Quick-lock collar system', 'Anti-roll hexagonal plates'],
    specs: [['Total weight', '20 kg pair'], ['Material', 'Cast iron, rubber coated']], tags: ['strength'],
  },
  {
    n: 'Insulated Steel Water Bottle 1L', brand: 'Stride', cat: 'cat_sports', price: 749, cmp: 999, icon: 'droplet', hue: 185,
    rating: 4.7, reviews: 1420, stock: 220, days: 33,
    desc: 'Double-wall vacuum insulated bottle that keeps drinks cold for 24 hours and hot for 12. Leak-proof lid, fits car cup holders.',
    hl: ['Cold 24 hrs / hot 12 hrs', 'BPA-free 18/8 stainless steel', 'Leak-proof, cup-holder friendly'],
    specs: [['Capacity', '1 L'], ['Weight', '390 g']],
    opts: [{ name: 'Colour', values: ['Matte Black', 'Sky', 'Rose'] }], tags: ['hydration'],
  },
  {
    n: 'Creative Building Blocks (500 pcs)', brand: 'Playhouse', cat: 'cat_toys', price: 1299, cmp: 1899, icon: 'blocks', hue: 48,
    rating: 4.8, reviews: 540, stock: 85, feat: true, days: 11,
    desc: '500 colourful, compatible bricks with a storage box and idea booklet. Builds fine motor skills and imagination for ages 5+.',
    hl: ['500 pieces + idea booklet', 'Compatible with major brick brands', 'BPA-free ABS, smooth edges'],
    specs: [['Pieces', '500'], ['Age', '5+ years']], tags: ['creative', 'kids'],
  },
  {
    n: 'Remote Control Racing Car', brand: 'Playhouse', cat: 'cat_toys', price: 1699, cmp: 2299, icon: 'car', hue: 8,
    rating: 4.3, reviews: 233, stock: 33, days: 55,
    desc: '1:16 scale 4WD off-road racer with a 2.4 GHz remote, up to 25 km/h top speed and a rechargeable battery for 30 minutes of play.',
    hl: ['4WD, up to 25 km/h', '2.4 GHz remote with 80 m range', 'Rechargeable battery, 30 min play'],
    specs: [['Scale', '1:16'], ['Age', '8+ years']], tags: ['rc', 'kids'],
  },
  {
    n: 'Strategy Night Board Game', brand: 'Playhouse', cat: 'cat_toys', price: 1099, cmp: 1499, icon: 'dice', hue: 280,
    rating: 4.6, reviews: 312, stock: 51, days: 22,
    desc: 'A quick-to-learn strategy game for 2–5 players. 45 minutes of trading, building and friendly betrayal.',
    hl: ['2–5 players, 45 min', 'Easy to learn, hard to master', 'Premium wooden components'],
    specs: [['Players', '2–5'], ['Age', '10+']], tags: ['family'],
  },
];

const NOW = Date.parse('2026-09-19T00:00:00Z');

export const productsSeed: Product[] = seeds.map((s, i) => ({
  id: `prod_${String(i + 1).padStart(3, '0')}`,
  slug: slugify(s.n),
  name: s.n,
  brand: s.brand,
  sku: `${s.brand.slice(0, 3).toUpperCase()}-${1000 + i * 7}`,
  description: s.desc,
  highlights: s.hl,
  specs: s.specs.map(([label, value]) => ({ label, value })),
  price: s.price,
  compareAtPrice: s.cmp,
  categoryId: s.cat,
  rating: s.rating,
  reviewCount: s.reviews,
  stock: s.stock,
  lowStockThreshold: 10,
  images: [],
  icon: s.icon,
  hue: s.hue,
  options: s.opts ?? [],
  tags: s.tags ?? [],
  featured: !!s.feat,
  createdAt: new Date(NOW - s.days * 86_400_000).toISOString(),
}));

const authors = ['Rahul Sharma', 'Priya Nair', 'Ankit Verma', 'Sneha Iyer', 'Mohammed Farhan', 'Kavya Reddy', 'Arjun Mehta', 'Divya Menon'];
const templates: [number, string, string][] = [
  [5, 'Excellent quality', 'Quality is excellent and delivery was fast. Exactly as described — would happily buy again.'],
  [5, 'Worth every rupee', 'Great value for the price. Build quality feels premium and it works flawlessly.'],
  [4, 'Very good, minor nitpicks', 'Really solid overall. Packaging could be better but the product itself is great.'],
  [4, 'Happy with the purchase', 'Does what it promises. Setup was easy and it looks even better in person.'],
  [3, 'Decent', 'Good for the price, though I expected slightly better finish. Customer support was helpful.'],
];

export function reviewsFor(productId: string): Review[] {
  const seed = Number(productId.replace(/\D/g, '')) || 1;
  return templates.map(([rating, title, body], i) => ({
    id: `rev_${productId}_${i}`,
    productId,
    author: authors[(seed + i * 3) % authors.length],
    rating,
    title,
    body,
    date: new Date(NOW - (i * 9 + seed) * 86_400_000).toISOString(),
    verified: i !== 4,
    helpful: ((seed * (i + 3)) % 40) + 2,
  }));
}
